import * as React from "react";
import { Trans, useTranslation } from "react-i18next";

import { acceptTerms, apiErrorMessage } from "@/api/client";
import { useSession } from "@/auth/session/SessionProvider";
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "@/lib/legalLinks";

import { useProfile } from "../profile/ProfileContext";

/**
 * The blocking terms gate: a signed-in user who has not accepted the current version of the Terms
 * of Service cannot use the app until they do.
 *
 * <p><b>Why a gate and not a checkbox on the setup page.</b> A checkbox at setup would only ever
 * catch people who signed up after it shipped, and would do nothing at all when the terms change.
 * This keys on {@code termsAccepted}, which the server computes per user per version, so every
 * existing account is caught on its next load, and bumping
 * {@code LegalDocuments.CUSTOMER_TERMS_VERSION} re-gates everyone. That is the mechanism behind the
 * promise in {@code terms.changes}.</p>
 *
 * <p>It also gives the beta approval gate a successor. Today an unapproved user is stopped by
 * {@code ApprovalGate}; when the beta ends and that gate is switched off, this one is what still
 * stands between a new account and the app.</p>
 *
 * <p><b>Deliberately not dismissable.</b> No close button, no escape key, no click-outside. There
 * is no version of "later" here: the point is that the account is unusable until the answer is
 * recorded. The only way past without accepting is to sign out, which is offered plainly so nobody
 * is trapped.</p>
 *
 * <p>Mounted INSIDE {@code ApprovalGate}, so a waitlisted user sees the waitlist rather than being
 * asked to agree to terms for a service they cannot use yet, and OUTSIDE the route tree, so it
 * covers {@code /profile/setup} too. A new user therefore meets it on the first screen they see.</p>
 */
export function TermsGate({ children }: { children: React.ReactNode }) {
  const { state, setProfile } = useProfile();

  // Anything other than a loaded profile is somebody else's problem: ApprovalGate holds the app
  // back while loading, and RequireHandle owns the error state and its retry.
  if (state.status !== "ready" || state.profile.termsAccepted) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <TermsDialog version={state.profile.termsVersionRequired} onAccepted={setProfile} />
    </>
  );
}

function TermsDialog({
  version,
  onAccepted,
}: {
  version: string;
  onAccepted: (profile: import("@/api/types").ProfileView) => void;
}) {
  const { t } = useTranslation();
  const session = useSession();
  const [ticked, setTicked] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // The page behind must not scroll while this is up. Restoring the previous value rather than
  // clearing it matters: something else may already own overflow when this mounts.
  React.useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      // The response IS the refreshed profile, so accepting drops the gate without a second fetch.
      onAccepted(await acceptTerms(version));
    } catch (e) {
      setError(apiErrorMessage(e, t("termsGate.failed")));
      setBusy(false);
    }
  };

  const rich = {
    terms: (
      <a
        href={TERMS_OF_SERVICE_URL}
        target="_blank"
        rel="noreferrer"
        className="font-semibold underline underline-offset-2"
      />
    ),
    privacy: (
      <a
        href={PRIVACY_POLICY_URL}
        target="_blank"
        rel="noreferrer"
        className="font-semibold underline underline-offset-2"
      />
    ),
  };

  return (
    // aria-modal + role=dialog so a screen reader treats the app behind as inert, which is what
    // the visual dimming says to everyone else.
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-gate-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
    >
      <div className="comic-shadow w-full max-w-lg rounded-lg border-2 border-black bg-card p-6 font-comic-body">
        <h2
          id="terms-gate-title"
          className="font-comic-headline text-lg uppercase tracking-wide text-foreground"
        >
          {t("termsGate.title")}
        </h2>

        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {t("termsGate.body")}
        </p>

        <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-md border-2 border-black bg-muted/40 p-3">
          <input
            type="checkbox"
            checked={ticked}
            disabled={busy}
            onChange={(e) => setTicked(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-brand-blue"
          />
          <span className="text-sm leading-relaxed text-foreground">
            <Trans i18nKey="termsGate.accept" components={rich} />
          </span>
        </label>

        <p className="mt-2 text-[11px] text-muted-foreground">
          {t("termsGate.version", { version })}
        </p>

        {error && (
          <div
            role="alert"
            className="mt-3 rounded-md border-2 border-black bg-red-100 px-3 py-2 text-sm text-red-900"
          >
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          {/* Always available. A gate with no way out is a trap, and somebody who will not accept
              has to be able to leave rather than be stuck on a dead screen. */}
          <button
            type="button"
            onClick={() => void session.signOut()}
            disabled={busy}
            className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
          >
            {t("termsGate.signOut")}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!ticked || busy}
            className="comic-shadow-sm flex h-[44px] items-center gap-2 rounded-md border-2 border-black bg-brand-blue px-5 font-comic-headline uppercase tracking-wide text-white hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-x-0 disabled:hover:translate-y-0"
          >
            {busy ? t("termsGate.saving") : t("termsGate.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
